import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import { AppSyncFarm, MonitorVaults } from 'common/models/farm';
import { QueryItemResponse, QueryItemsResponse } from 'common/models/graphql';
import { AppSyncUserInfo } from 'common/models/userInfo';
import {
  API_GATEWAY_URL,
  REFRESH_INTERVALS,
  UPDATE_POOL_URL,
} from 'constants/constants';
import {
  getUserInfoQuery,
  GetUserInfoVariables,
} from 'graphql/queries/getUserInfo.query';
import useParsedQueryString from 'hooks/useParsedQueryString';
import React, { FC, useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import debounce from 'lodash/debounce';
import { listMonitorVaultsQuery } from 'graphql/queries/listMonitorVaults.query';
import { FarmUtils } from 'utils/farmUtils';
import Vault from 'common/components/Vault/Vault';
import { TokenUtils } from 'utils/tokenUtils';
import { listFarmsQuery } from 'graphql/queries/listFarmsAll.query';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';
import { RouteComponentProps } from '@reach/router';

const VaultsByBaseTokenContainer: FC<RouteComponentProps> = (
  props: RouteComponentProps<{
    token?: string;
    pairedtoken?: string;
    tab?: string;
    address?: string;
  }>
) => {
  const { chainId } = useSelectedChain();
  const { address: accountAddress } = useAccount();

  const [initialFetch, setInitialFetch] = useState(false);

  // 2 ways of adding token or vault address into URL - as query string and as RouteComponentProps:
  // vault/?token=0x1234567... and vault/token/0x1234567....
  const {
    token: urlToken1,
    pairedtoken: urlPairedToken1,
    tab: urlTab1,
    address: urlAddress1,
  } = useParsedQueryString();

  const urlToken = urlToken1 ?? props.token;
  const urlPairedToken = urlPairedToken1 ?? props.pairedtoken;
  const urlTab = urlTab1 ?? props.tab;
  const urlAddress = urlAddress1 ?? props.address;

  // TODO: Handle loading and error
  const { data: dataFarms, refetch: refetchFarms } = useQuery<
    QueryItemsResponse<AppSyncFarm>
  >(listFarmsQuery(), {
    // Fetch the list of farms if there is no urlPoolId
    fetchPolicy: 'cache-and-network',
    pollInterval: REFRESH_INTERVALS['listFarms'],
  });
  const { data: dataMonitorVaults } = useQuery<
    QueryItemsResponse<MonitorVaults>
  >(listMonitorVaultsQuery, {
    fetchPolicy: 'cache-and-network',
    pollInterval: REFRESH_INTERVALS['listMonitorVaults'],
  });
  const [
    loadUserInfo,
    {
      data: dataUserInfo,
      networkStatus: networkStatusUserInfo,
      refetch: refetchUserInfo,
    },
  ] = useLazyQuery<QueryItemResponse<AppSyncUserInfo>, GetUserInfoVariables>(
    getUserInfoQuery,
    {
      // https://github.com/apollographql/react-apollo/issues/321
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
      variables: {
        user: accountAddress as string,
        chainId: chainId,
      },
      pollInterval: REFRESH_INTERVALS['userInfo'],
    }
  );

  const { get } = useFetch(API_GATEWAY_URL, {
    mode: 'no-cors',
  });

  const updatePool = async (poolId: number): Promise<void> => {
    try {
      await get(`${UPDATE_POOL_URL}?poolId=${poolId}`);
      await refetchFarms();
    } catch (e) {
      console.warn('Could not update product farms', e);
    }
  };

  const debouncedUpdatePool = useMemo(
    () => debounce(updatePool, 750),
    []
  ) as unknown as (poolId: number) => Promise<void>;

  const executeRefetchUserInfo = async (): Promise<any> => {
    if (accountAddress && refetchUserInfo) {
      await refetchUserInfo();
    }
  };

  const debouncedExecuteRefetchUserInfo = debounce(
    executeRefetchUserInfo,
    750
  ) as unknown as () => Promise<void>;

  const executeLoadUserInfo = async (): Promise<any> => {
    if (accountAddress) {
      await loadUserInfo();
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (chainId && accountAddress) {
      void executeLoadUserInfo();
    }
  }, [accountAddress, chainId]);

  const monitorVaults: MonitorVaults[] =
    dataMonitorVaults?.listMonitorVaults.items || [];
  const appSyncFarms: AppSyncFarm[] =
    dataFarms?.listFarms?.items
      .filter(
        (item) =>
          item.isPosition &&
          !item.isHidden &&
          (item.chainId === chainId ||
            item.lpAddress === urlAddress?.toString())
      )
      .sort((a, b) => {
        // return b.tvl - a.tvl;
        return (
          (FarmUtils.getIrrByVaultAddress(
            monitorVaults,
            b.farm.farmAddress ?? ''
          ) ?? 0) -
          (FarmUtils.getIrrByVaultAddress(
            monitorVaults,
            a.farm.farmAddress ?? ''
          ) ?? 0)
        );
      }) || [];
  const appSyncUserInfo: AppSyncUserInfo = dataUserInfo?.userInfo || {
    farms: [],
    tokens: [],
  };

  const tokenAddress = urlToken as string;
  const pairedTokenAddress = urlPairedToken as string;
  let depositToken = FarmUtils.getAppSyncFarmTokenByAddress(
    appSyncFarms,
    tokenAddress?.toLowerCase(),
  );
  let pairedToken = FarmUtils.getPairedTokenByAddress(
    appSyncFarms,
    tokenAddress,
    pairedTokenAddress?.toLowerCase(),
  );
  let vault = FarmUtils.getVaultByTokens(
    appSyncFarms,
    tokenAddress?.toLowerCase(),
    pairedTokenAddress?.toLowerCase(),
  );

  const vaultsByDepositToken = appSyncFarms.filter(
    (v) => FarmUtils.getBaseToken(v).address.toLowerCase() === depositToken?.address.toLowerCase()
  );
  if (vaultsByDepositToken.length === 1) {
    vault = vaultsByDepositToken[0];
    pairedToken = FarmUtils.getRewardToken(vault);
  }
  const vaultsByAddress = appSyncFarms.filter(
    (v) => v.lpAddress === urlAddress
  );
  if (vaultsByAddress.length > 0) {
    vault = vaultsByAddress[0];
    depositToken = FarmUtils.getBaseToken(vault);
    pairedToken = FarmUtils.getRewardToken(vault);
  }

  const farmUserInfo = vault
    ? TokenUtils.getFarmUserInfo(vault, appSyncUserInfo)
    : undefined;
  const userAmount = farmUserInfo?.availableLP;
  let tab = 'Get Started';
  // if (vault) {
  //   tab = 'Deposit';
  // }
  if (userAmount && Number(userAmount) > 0) {
    tab = 'Manage';
  }
  if (urlTab){
    tab = urlTab.toString();
  }
  useEffect(() => {
    if (!initialFetch) {
      if (dataUserInfo) {
        setInitialFetch(true);
      }
    }
  }, [dataUserInfo]);

  if (
    (urlToken || urlAddress) &&
    appSyncFarms.length > 0 &&
    (initialFetch || !accountAddress)
  ) {
    return (
      <Vault
        tab={tab}
        accountAddress={accountAddress ?? ''}
        token={depositToken}
        pairedToken={pairedToken}
        vault={vault}
        farms={appSyncFarms}
        userInfoLoading={networkStatusUserInfo === NetworkStatus.refetch}
        userInfo={appSyncUserInfo}
        claiming={false}
        monitorVaults={monitorVaults}
        chainId={chainId}
        refetchUserInfo={debouncedExecuteRefetchUserInfo}
        updatePool={debouncedUpdatePool}
        urlTab={urlTab as string}
      />
    );
  } else {
    return null;
  }
};

export default VaultsByBaseTokenContainer;
